// 防抖
export const debounce = (func, wait) => {
  let timeout
  return function () {
    const context = this
    const args = arguments
    const later = function () {
      timeout = null
      func.apply(context, args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// 节流
export const throttle = (func, wait) => {
  let context, args, timeout, throttling, more
  const whenDone = debounce(function () {
    more = throttling = false
  }, wait)
  return function () {
    context = this
    args = arguments
    const later = function () {
      timeout = null
      if (more) func.apply(context, args)
      whenDone()
    }
    if (!timeout) timeout = setTimeout(later, wait)
    if (throttling) {
      more = true
    } else {
      func.apply(context, args)
    }
    whenDone()
    throttling = true
  }
}

// 重量计算保留2位小数
export const calcWeight = (weight) => {
  if (isNaN(Number(weight))) {
    return '0.00'
  } else {
    return Number(weight).toFixed(2)
  }
}
/**
 * 深拷贝对象
 * @param {any} obj 要拷贝的对象
 * @returns {any} 拷贝后的对象
 */
export function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj.getTime())
  if (obj instanceof Array) return obj.map(item => deepClone(item))
  if (typeof obj === 'object') {
    const clonedObj = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        clonedObj[key] = deepClone(obj[key])
      }
    }
    return clonedObj
  }
}

/**
 * 生成UUID
 * @param {number} length UUID长度
 * @returns {string} UUID字符串
 */
export function generateUUID(length = 32) {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)]
  }
  return result
}

/**
 * 格式化文件大小
 * @param {number} bytes 字节数
 * @returns {string} 格式化后的文件大小
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * 格式化日期
 * @param {Date|string|number} date 日期
 * @param {string} format 格式字符串
 * @returns {string} 格式化后的日期字符串
 */
export function formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
  if (!date) return ''
  
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''

  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')

  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}

/**
 * 移除对象中的空值
 * @param {Object} obj 要处理的对象
 * @returns {Object} 处理后的对象
 */
export function removeEmptyFields(obj) {
  const result = {}
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]
      if (value !== null && value !== undefined && value !== '') {
        result[key] = value
      }
    }
  }
  return result
}

/**
 * 获取对象属性值
 * @param {Object} obj 对象
 * @param {Array} keys 属性键数组
 * @param {boolean} required 是否必需
 * @returns {Object} 提取的属性对象
 */
export function getProps(obj, keys, required = true) {
  const result = {}
  keys.forEach(key => {
    if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = obj[key]
    } else if (required) {
      console.warn(`Missing required prop: ${key}`)
    }
  })
  return result
}

/**
 * 检查是否为全屏模式
 * @returns {boolean} 是否为全屏
 */
export function isFullScreen() {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  )
}

/**
 * 数组去重
 * @param {Array} arr 要去重的数组
 * @param {string} key 去重依据的键名（对象数组）
 * @returns {Array} 去重后的数组
 */
export function uniqueArray(arr, key) {
  if (!Array.isArray(arr)) return []
  
  if (key) {
    const seen = new Set()
    return arr.filter(item => {
      const value = item[key]
      if (seen.has(value)) {
        return false
      }
      seen.add(value)
      return true
    })
  }
  
  return [...new Set(arr)]
}

/**
 * 下载文件
 * @param {string} url 文件URL
 * @param {string} filename 文件名
 */
export function downloadFile(url, filename) {
  const link = document.createElement('a')
  link.href = url
  link.download = filename || 'download'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
export function formatDays(value) {
  if (value == null || isNaN(value)) return "";

  const intPart = Math.floor(value); // 向下取整
  const ceilPart = Math.ceil(value); // 向上取整

  if (intPart === ceilPart) {
    // 正好是整数
    return `${intPart}天`;
  } else {
    // 小数，输出范围
    return `${intPart}-${ceilPart}天`;
  }
}
/**
 * 复制文本到剪贴板
 * @param {string} text 要复制的文本
 * @returns {Promise<boolean>} 是否复制成功
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    } else {
      // 降级方案
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      const result = document.execCommand('copy')
      document.body.removeChild(textArea)
      return result
    }
  } catch (error) {
    console.error('复制失败:', error)
    return false
  }
}
// 校验是不是正整数
export const isPositiveInteger = num => /(^[1-9]\d*$)/.test(num)

/**
 * 物料排序工具函数
 * 将 materialsType 为 1 的物料排在前面
 * @param {Array} list - 物料列表
 * @returns {Array} 排序后的新列表
 */
export const sortByMaterialsType = (list) => {
  if (!list || !Array.isArray(list)) return []
  
  return [...list].sort((a, b) => {
    const typeA = a.materialsType || 0
    const typeB = b.materialsType || 0
    // materialsType === 1 的优先级最高,排在前面
    if (typeA === 1 && typeB !== 1) return -1
    if (typeA !== 1 && typeB === 1) return 1
    return 0 // 保持原有顺序
  })
}

// 加法函数，用来得到精确的加法结果
export const add = (arg1, arg2) => {
  let r1, r2, m, c
  try {
    r1 = arg1.toString().split('.')[1].length
  } catch (e) {
    r1 = 0
  }
  try {
    r2 = arg2.toString().split('.')[1].length
  } catch (e) {
    r2 = 0
  }
  c = Math.abs(r1 - r2)
  m = Math.pow(10, Math.max(r1, r2))
  if (c > 0) {
    const cm = Math.pow(10, c)
    if (r1 > r2) {
      arg1 = Number(arg1.toString().replace('.', ''))
      arg2 = Number(arg2.toString().replace('.', '')) * cm
    } else {
      arg1 = Number(arg1.toString().replace('.', '')) * cm
      arg2 = Number(arg2.toString().replace('.', ''))
    }
  } else {
    arg1 = Number(arg1.toString().replace('.', ''))
    arg2 = Number(arg2.toString().replace('.', ''))
  }
  return (arg1 + arg2) / m
}

// 减法函数，用来得到精确的减法结果
export const sub = (arg1, arg2) => {
  let r1, r2, m, n
  try {
    r1 = arg1.toString().split('.')[1].length
  } catch (e) {
    r1 = 0
  }
  try {
    r2 = arg2.toString().split('.')[1].length
  } catch (e) {
    r2 = 0
  }
  m = Math.pow(10, Math.max(r1, r2))
  n = (r1 >= r2) ? r1 : r2
  return ((arg1 * m - arg2 * m) / m).toFixed(n)
}